import { Badge, Text } from "@alpacahq/ui/old";

import { Card } from "~/v2/components";
import { PDFAgreementModal } from "~/v2/components/form";
import { AGREEMENTS } from "~/v2/constants";
import type { AgreementEntry } from "./utils";

export interface AccountAgreementCardPresentationProps {
  cryptoEnabled: boolean;
  isCryptoSigned: boolean;
  isCustomerSigned: boolean;
  isOptionsSigned: boolean;
  cryptoAgreements: AgreementEntry[];
  customerAgreements: AgreementEntry[];
  optionsAgreements: AgreementEntry[];
  isModalOpen: boolean;
  isSubmitting: boolean;
  isMissingSignerPartyId: boolean;
  onView: () => void;
  onCancelModal: () => void;
  onAgree: () => void;
}

const AgreementList = ({ agreements }: { agreements: AgreementEntry[] }) => {
  if (agreements.length === 0) {
    return null;
  }

  return (
    <div className="my-2 ml-2 flex flex-col gap-2">
      {agreements.map(({ agreement, revision }) => (
        <div
          key={`${agreement}-${revision}`}
          className="flex items-center gap-2"
        >
          <Text className="text-sm">v{revision}</Text>
          <Badge variant="v2/emerald">Signed</Badge>
        </div>
      ))}
    </div>
  );
};

export const AccountAgreementCardPresentation = ({
  cryptoEnabled,
  isCryptoSigned,
  isCustomerSigned,
  isOptionsSigned,
  cryptoAgreements,
  customerAgreements,
  optionsAgreements,
  isModalOpen,
  isSubmitting,
  isMissingSignerPartyId,
  onView,
  onCancelModal,
  onAgree,
}: AccountAgreementCardPresentationProps) => (
  <section>
    <Card title="Agreements">
      {cryptoEnabled && (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Text className="font-medium text-black text-sm">
              Crypto Agreement
            </Text>
            {!isCryptoSigned && <Badge variant="v2/red">Unsigned</Badge>}
          </div>
          <AgreementList agreements={cryptoAgreements} />
          <div className="mt-1 text-sm">
            {!isCryptoSigned && !isMissingSignerPartyId && (
              <>
                <button
                  type="button"
                  className="cursor-pointer underline"
                  onClick={onView}
                >
                  View
                </button>
                {", "}
              </>
            )}
            <a
              href={AGREEMENTS.crypto_agreement.url}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Download
            </a>
          </div>
        </div>
      )}

      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <Text className="font-medium text-black text-sm">
            Customer Agreement
          </Text>
          {!isCustomerSigned && <Badge variant="v2/red">Unsigned</Badge>}
        </div>
        <AgreementList agreements={customerAgreements} />
        <div className="mt-1 text-sm">
          <a
            href={AGREEMENTS.customer_agreement.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Download
          </a>
        </div>
      </div>

      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <Text className="font-medium text-black text-sm">
            Option Agreement
          </Text>
          {!isOptionsSigned && <Badge variant="v2/red">Unsigned</Badge>}
        </div>
        <AgreementList agreements={optionsAgreements} />
        <div className="mt-1 text-sm">
          <a
            href={AGREEMENTS.options_agreement.url}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Download
          </a>
        </div>
      </div>
    </Card>

    <PDFAgreementModal
      isOpen={isModalOpen}
      title="Alpaca Crypto Customer Agreement"
      url={AGREEMENTS.crypto_agreement.url}
      onCancel={onCancelModal}
      onAgree={onAgree}
      hasAgreed={isCryptoSigned}
      isSubmitting={isSubmitting}
    />
  </section>
);
